import React, { useMemo } from 'react';
import { Typography, Popover, Badge } from 'antd';
import styled from 'styled-components';
import { NavLink } from '@/shared/navigation';
import { useLocation } from 'react-router-dom';

const Icon = styled.i`
  color: var(--gray-7);
  font-size: 24px;
  @media screen and (max-width: 768px) {
    font-size: 28px;
  }
`;

const StyledNavLink = styled(NavLink)<{ isMobile?: boolean }>`
  margin-bottom: ${props => (props.isMobile ? '8px' : '0')} !important;
  display: block !important;
  text-decoration: none !important;
`;

const Root = styled.div<{ active: boolean; isMobile?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-direction: column;
  border-radius: 4px;
  background: var(--brand-surface-lihgt);
  padding: 4px;
  position: relative;
  ${props =>
    props.isMobile
      ? `
   flex-direction: row;
   gap: 10px;
   padding: 4px 14px;
   min-height: 48px;
   height: 48px;

  `
      : ''}
  &:hover {
    background: var(--green-2);
    > i,
    > span {
      color: var(--gray-9) !important;
    }
    &::before {
      background: var(--green-2);
    }
  }
  &::after {
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: var(--green-6);
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    z-index: 1;
  }
  &::before {
    position: absolute;
    top: 0;
    left: 2px;
    width: 4px;
    height: 100%;
    background: #dff9e4;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    z-index: 2;
  }
  ${props =>
    props.active
      ? `
    background: #DFF9E4;
    margin-left: 2px;
    > i,
    > span {
      color: var(--green-6) !important;
    }
     
    &::before {
      background: #DFF9E4;
    }
    &:after,
    &:before {
      content: '';
    }
  `
      : ''}
`;

const Text = styled(Typography.Text)`
  color: var(--gray-7) !important;
  font: var(--font-semibold-12);
  @media screen and (max-width: 768px) {
    font: var(--font-normal-18);
  }
`;

interface Props {
  isMobile?: boolean;
  title: string;
  icon: string;
  paths: (string | RegExp)[];
  to?: string;
  badge?: string | number | null;
}

export const CollapsibleMenuItem: React.FunctionComponent<Props> = ({ to, title, icon, paths, children, isMobile, badge }) => {
  const location = useLocation();

  const active = useMemo(() => {
    return !!paths.find(path => (path instanceof RegExp ? path.test(location.pathname) : location.pathname.includes(path)));
  }, [paths, location.pathname]);

  if (to) {
    return (
      <StyledNavLink to={to} isMobile={isMobile}>
        <Root data-testID={`sidebar-${title}`} active={active} isMobile={isMobile}>
          <StyledBadge count={badge} size="small">
            <Icon className={`icon-${icon}`} />
          </StyledBadge>
          <Text ellipsis={{ tooltip: title }}>{title}</Text>
        </Root>
      </StyledNavLink>
    );
  }
  return (
    <Popover
      placement="rightTop"
      title={title}
      content={children}
      trigger={isMobile ? 'none' : 'hover'}
      destroyTooltipOnHide
      overlayClassName="menu-popover"
      key={location.pathname}
    >
      <Root data-testID={`sidebar-${title}`} active={active} isMobile={isMobile}>
        <Icon className={`icon-${icon}`} />
        <Text ellipsis={{ tooltip: title }}>{title}</Text>
      </Root>
    </Popover>
  );
};

const StyledBadge = styled(Badge)`
  .ant-badge-count {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
`;
